// Shared barcode-label constants, types, and the persisted default label
// settings used by both the Barcode Settings screen (content_barcode_setting_layout)
// and the Barcode Printing screen (fragment_barcode_setting).

import { cachedKey } from './idb';

// Label sizes from radio_group_label (visibility="gone" entries omitted).
export const LABEL_SIZES = [
  '35mm * 15mm', '38mm * 15mm', '30mm * 15mm', '50mm * 25mm [1]', '50mm * 25mm [2]',
  '50mm * 75mm', '75mm * 50mm', '100mm * 100mm', '25mm * 12mm', '33mm * 33mm [3]',
  '55mm * 12mm', '35mm * 15mm [2]', '38mm * 25mm [2]', '35mm * 22mm [3]', '50mm * 40mm [2]',
];

// Any "W mm * H mm" string works with sizeToMm/sizeToPx, so a custom size is
// just a label built from the two numbers the user typed.
export const customLabelSize = (w: number, h: number): string => `${w}mm * ${h}mm`;

export const POSITIONS = ['Top', 'Bottom'] as const;
export type Position = (typeof POSITIONS)[number];

// Customize Content options (customize_barcode_dialog.xml / label_opt list).
export type ContentKey =
  | 'shopName'
  | 'productName'
  | 'shortName'
  | 'price'
  | 'mrp'
  | 'purchasePrice'
  | 'hsn'
  | 'searchCode';

export const CONTENT_OPTIONS: { key: ContentKey; label: string }[] = [
  { key: 'shopName', label: 'Shop Name' },
  { key: 'productName', label: 'Product Name' },
  { key: 'shortName', label: 'Short Name' },
  { key: 'price', label: 'Price' },
  { key: 'mrp', label: 'MRP' },
  { key: 'purchasePrice', label: 'Purchase Price' },
  { key: 'hsn', label: 'HSN' },
  { key: 'searchCode', label: 'Search Code' },
];

// Approximate mm→px for the on-screen preview (1mm ≈ 3.4px).
export const sizeToPx = (label: string): { w: number; h: number } => {
  const m = label.match(/(\d+)mm\s*\*\s*(\d+)mm/);
  if (!m) return { w: 180, h: 90 };
  return { w: Math.round(parseInt(m[1]) * 3.4), h: Math.round(parseInt(m[2]) * 3.4) };
};

// Physical label size in mm (for the TSPL USB label printer).
export const sizeToMm = (label: string): { w: number; h: number } => {
  const m = label.match(/(\d+)mm\s*\*\s*(\d+)mm/);
  if (!m) return { w: 35, h: 15 };
  return { w: parseInt(m[1]), h: parseInt(m[2]) };
};

// Which command language the label printer speaks. 'auto' picks TSPL when a
// dedicated label printer is selected and ESC/POS when labels fall back to the
// receipt printer.
export const LABEL_LANGUAGES = ['auto', 'tspl', 'escpos'] as const;
export type LabelLanguage = (typeof LABEL_LANGUAGES)[number];

// Emphasis for the Price line — bold and/or a bigger font, on the printed
// label and in the previews.
export interface PriceStyle {
  bold: boolean;
  /** Font size multiplier: 1 normal, 2 large, 3 extra large. */
  scale: 1 | 2 | 3;
}

export interface BarcodeLabelSettings {
  labelSize: string;
  labelLanguage: LabelLanguage;
  priceStyle: PriceStyle;
  /** Blank space left at the top of the label, in mm. */
  marginTopMm: number;
  /** Blank space left at the left edge of the label, in mm. */
  marginLeftMm: number;
  /** Blank space kept at the bottom of the label, in mm. */
  marginBottomMm: number;
  /** Blank space kept at the right edge of the label, in mm. */
  marginRightMm: number;
  /** Labels side by side across the roll (2-up stock). */
  labelsAcross: number;
  /** Gap between those columns, in mm. */
  columnGapMm: number;
  /** Vertical gap between labels on the roll, in mm. */
  labelGapMm: number;
  position: Position;
  withoutPrice: boolean;
  content: Record<ContentKey, boolean>;
}

export const DEFAULT_BARCODE_SETTINGS: BarcodeLabelSettings = {
  labelSize: LABEL_SIZES[0],
  labelLanguage: 'auto',
  priceStyle: { bold: true, scale: 1 },
  marginTopMm: 4,
  marginLeftMm: 5,
  marginBottomMm: 0,
  marginRightMm: 0,
  labelsAcross: 1,
  columnGapMm: 2,
  labelGapMm: 2,
  position: 'Top',
  withoutPrice: false,
  content: {
    shopName: true,
    productName: true,
    shortName: false,
    price: true,
    mrp: false,
    purchasePrice: false,
    hsn: false,
    searchCode: false,
  },
};

const barcodeCache = cachedKey<Partial<BarcodeLabelSettings>>('sconnect.barcodeSettings', {});

export function getBarcodeSettings(): BarcodeLabelSettings {
  const saved = barcodeCache.get();
  return {
    ...DEFAULT_BARCODE_SETTINGS,
    ...saved,
    priceStyle: { ...DEFAULT_BARCODE_SETTINGS.priceStyle, ...(saved.priceStyle ?? {}) },
    content: { ...DEFAULT_BARCODE_SETTINGS.content, ...(saved.content ?? {}) },
  };
}

export function saveBarcodeSettings(s: BarcodeLabelSettings): void {
  barcodeCache.set(s);
}
